import type { Address, Customer } from "@expert/sales-tools";
import type { Partner } from "@expert/shared-types";

import type { ChannelKind } from "@/sdk";

export const EligibilityStatus = Object.freeze({
    Ineligible: "INELIGIBLE",
    Eligible: "ELIGIBLE",
    Pending: "PENDING_ENROLLMENT",
    Enrolled: "ALREADY_ENROLLED",
    Unknown: "UNKNOWN",
});
export type EligibilityStatus = (typeof EligibilityStatus)[keyof typeof EligibilityStatus];

export const EnrollmentStatus = Object.freeze({
    Success: "SUCCESS",
    Error: "ERROR",
    Failure: "FAILURE",
});
export type EnrollmentStatus = (typeof EnrollmentStatus)[keyof typeof EnrollmentStatus];

export const SaleProgress = Object.freeze({
    InProgress: "INPROGESS", // Note: Typo
    Success: "SUCCESS",
    Failure: "FAILURE",
    Declined: "DECLINED",
});
export type SaleProgress = (typeof SaleProgress)[keyof typeof SaleProgress];

export interface SaleStatusStyle {
    bgColor: string;
    fontColor: string;
}

export const StatusStyles: Record<string, SaleStatusStyle> = {
    Success: {
        bgColor: "var(--mantine-color-success-5)",
        fontColor: "var(--mantine-color-dark-9)",
    },
    Neutral: {
        bgColor: "var(--mantine-color-dark-2)",
        fontColor: "white",
    },
    Error: {
        bgColor: "var(--mantine-color-red-8)",
        fontColor: "white",
    },
};

export interface SalesOpportunity {
    sessionId: string;
    partner: Partner;
}

export type CustomerInput = Pick<Customer, "firstName" | "lastName" | "email" | "accountNumber">;

export interface EligibilityAttributes {
    isInternationalCaller?: boolean;
    currentPlan?: string;
    chatSourceApp?: string;
    currentSubscriptions?: string[];
    expertLocation?: string;
    isCustomerInProtectedState?: boolean;
    client?: string;
    isPrecheck?: boolean;
}

export interface EligibilityRequest {
    partner: Partner;
    sessionId: string;
    customerMdn?: string;
    countryCallingCode?: string;
    reservationSid?: string;
    /** @deprecated - use customerInteractionId */
    asurionCallId?: string;
    /**
     * A channel-agnostic identifier representing the interaction with the customer via a CCaaS provider.
     * This could be a chat ID, call ID, or any other identifier that uniquely represents the customer interaction.
     */
    customerInteractionId?: string;
    address?: Address;
    customer?: CustomerInput;
    eligibilityAttributes?: EligibilityAttributes;
    salesChannel?: ChannelKind;
}

export interface Enrollment extends Required<SalesOpportunity> {
    productOfferId: string;
    confirmationCode?: string;
    productSku: string;
}

export interface Declination extends Required<SalesOpportunity> {
    productOfferId: string;
    productSku: string;
    reason?: string;
}
export interface EnrollmentResponse {
    enrollmentStatus: EnrollmentStatus;
    productSku: string;
}

export const SalesProvider = Object.freeze({
    ASP: "asp",
    EXPERT_SALES: "expert-sales",
    EXPERT_UNIVERSITY: "expert-university",
});
export type SalesProvider = (typeof SalesProvider)[keyof typeof SalesProvider];
